/* eslint react/jsx-props-no-spreading: 0 */
import { Box, Divider, FormControl, Stack, Typography } from '@mui/material';
import { Checkbox, FormStepComponentProps, TextField } from 'common';
import { Controller, useFormContext } from 'react-hook-form';
import { buyerSignUpFormText } from '../BuyerSignUp.constants';
import { BuyerSignupFormSchema } from '../BuyerSignUp.schema';
import { EnableDisableRadioGroupFormControl, StackWithAccordion } from '../../../components';
import { IDE_THRESHOLD_MAX, IDE_THRESHOLD_MIN } from '../../../constants';

const text = buyerSignUpFormText.buyerSettings;

type GeneralInformationProps = FormStepComponentProps<typeof BuyerSignupFormSchema>;

export const BuyerSettings = ({ control }: GeneralInformationProps) => {
    // react-hook-form hooks
    const { setValue, getValues, trigger } = useFormContext();

    // sections
    const nextGenSupplierEnablementSection = (
        <StackWithAccordion
            title={text.sections.nextGenSupplierEnablement.header}
            testId="next-gen-supplier-enablement-section"
            defaultExpanded={getValues('buyerSettings.infinxIntegration.useInfinxIntegration')}
        >
            <Controller
                name="buyerSettings.nextGenSupplier.ocr"
                control={control}
                render={({ field }) => (
                    <Checkbox
                        label={text.sections.nextGenSupplierEnablement.subSections.nextGenSupplierEnablementOCR.header}
                        testId="select-nextGenSupplier-check-enableOcr"
                        {...field}
                        checked={!!field.value}
                        value={field?.value?.toString()}
                        height="100%"
                    />
                )}
            />
            <Controller
                name="buyerSettings.nextGenSupplier.ideTemplateMatching"
                control={control}
                render={({ field, fieldState }) => (
                    <>
                        <EnableDisableRadioGroupFormControl
                            title={
                                text.sections.nextGenSupplierEnablement.subSections
                                    .nextGenSupplierEnablementIDETemplateMatching.header
                            }
                            field={field}
                            fieldState={fieldState}
                            onChange={(enabled) => {
                                setValue('buyerSettings.nextGenSupplier.ideTemplateMatching', enabled);
                                void trigger('buyerSettings.nextGenSupplier.threshold');
                            }}
                            testId="next-gen-supplier-ide-template-matching"
                            enableChild={
                                <FormControl fullWidth error={fieldState.invalid} sx={{ flex: 1 }}>
                                    <Controller
                                        name="buyerSettings.nextGenSupplier.threshold"
                                        control={control}
                                        render={({ field: fieldThreshold, fieldState: fieldStateThreshold }) => (
                                            <TextField
                                                testId="next-gen-supplier-threshold-text"
                                                // eslint-disable-next-line react/jsx-props-no-spreading
                                                {...fieldThreshold}
                                                error={fieldStateThreshold.invalid}
                                                helperText={fieldStateThreshold.error?.message}
                                                label="Threshold"
                                                type="number"
                                                inputProps={{
                                                    min: IDE_THRESHOLD_MIN,
                                                    max: IDE_THRESHOLD_MAX,
                                                }}
                                                labelBoxSx={{ width: '100%' }}
                                            />
                                        )}
                                    />
                                </FormControl>
                            }
                        />
                    </>
                )}
            />
        </StackWithAccordion>
    );

    const invoicesSection = (
        <StackWithAccordion title={text.sections.invoices.header} testId="invoices-section">
            <Controller
                name="buyerSettings.invoices.enable"
                control={control}
                render={({ field }) => (
                    <Checkbox
                        label={text.sections.invoices.subSections.invoicesEnableInvoices.header}
                        testId="select-invoices-check-enableInvoices"
                        {...field}
                        checked={!!field.value}
                        value={field?.value?.toString()}
                        height="100%"
                    />
                )}
            />
            <Controller
                name="buyerSettings.invoices.poFlip"
                control={control}
                render={({ field }) => (
                    <Checkbox
                        label={text.sections.invoices.subSections.invoicesPOFlip.header}
                        testId="select-invoices-check-enablePoFlip"
                        {...field}
                        checked={!!field.value}
                        value={field?.value?.toString()}
                        height="100%"
                    />
                )}
            />
            <Controller
                name="buyerSettings.invoices.poCloudMatching"
                control={control}
                render={({ field, fieldState }) => (
                    <EnableDisableRadioGroupFormControl
                        title={text.sections.invoices.subSections.invoicesPOCloudMatching.header}
                        field={field}
                        fieldState={fieldState}
                        onChange={(enabled) => {
                            setValue('buyerSettings.invoices.poCloudMatching', enabled);
                            if (!enabled) {
                                setValue('buyerSettings.invoices.poCloudMatchingForceOneLine', false);
                            }
                            void trigger('buyerSettings.invoices.poCloudMatchingForceOneLine');
                        }}
                        testId="select-invoices-poCloudMatching"
                        enableChild={
                            <FormControl fullWidth error={fieldState.invalid} sx={{ flex: 1 }}>
                                <Controller
                                    name="buyerSettings.invoices.poCloudMatchingForceOneLine"
                                    control={control}
                                    render={({ field: subField }) => (
                                        <Checkbox
                                            label={
                                                text.sections.invoices.subSections.invoicesPOCloudMatchingForceOneLine
                                                    .header
                                            }
                                            testId="select-invoices-check-enablePoCloudMatching"
                                            {...subField}
                                            checked={!!subField.value}
                                            value={subField?.value?.toString()}
                                            height="100%"
                                        />
                                    )}
                                />
                            </FormControl>
                        }
                    />
                )}
            />
        </StackWithAccordion>
    );

    const posForDeliverySection = (
        <StackWithAccordion title={text.sections.posForDelivery.header} testId="pos-for-delivery-section">
            <Controller
                name="buyerSettings.poForDelivery.enable"
                control={control}
                render={({ field }) => (
                    <Checkbox
                        label={text.sections.posForDelivery.subSections.posForDeliveryEnable.header}
                        testId="select-invoices-check-enablepoForDelivery"
                        {...field}
                        checked={!!field.value}
                        value={field?.value?.toString()}
                        height="100%"
                    />
                )}
            />
        </StackWithAccordion>
    );

    const poaSection = (
        <StackWithAccordion title={text.sections.poa.header} testId="poa-section">
            <Controller
                name="buyerSettings.poa.enable"
                control={control}
                render={({ field }) => (
                    <Checkbox
                        label={text.sections.poa.subSections.poaEnable.header}
                        testId="select-invoices-check-enablePoa"
                        {...field}
                        checked={!!field.value}
                        value={field?.value?.toString()}
                        height="100%"
                    />
                )}
            />
        </StackWithAccordion>
    );

    const asnSection = (
        <StackWithAccordion title={text.sections.asn.header} testId="poa-section">
            <Controller
                name="buyerSettings.asn.enable"
                control={control}
                render={({ field }) => (
                    <Checkbox
                        label={text.sections.asn.subSections.asnEnable.header}
                        testId="select-invoices-check-enableAsn"
                        {...field}
                        checked={!!field.value}
                        value={field?.value?.toString()}
                        height="100%"
                    />
                )}
            />
        </StackWithAccordion>
    );

    const workflowSection = (
        <StackWithAccordion title={text.sections.workflow.header} testId="workflow-section">
            <Controller
                name="buyerSettings.workflow.cloudCode"
                control={control}
                render={({ field }) => (
                    <Checkbox
                        label={text.sections.workflow.subSections.workflowCouldCode.header}
                        testId="workflow-cloud-code"
                        {...field}
                        checked={!!field.value}
                        value={field?.value?.toString()}
                        height="100%"
                    />
                )}
            />
            <Controller
                name="buyerSettings.workflow.referencePO"
                control={control}
                render={({ field }) => (
                    <Checkbox
                        label={text.sections.workflow.subSections.workflowReferencePO.header}
                        testId="workflow-referencePO"
                        {...field}
                        checked={!!field.value}
                        value={field?.value?.toString()}
                        height="100%"
                    />
                )}
            />
            <Controller
                name="buyerSettings.workflow.referenceNonPO"
                control={control}
                render={({ field }) => (
                    <Checkbox
                        label={text.sections.workflow.subSections.workflowReferenceNonPO.header}
                        testId="workflow-referenceNonPO"
                        {...field}
                        checked={!!field.value}
                        value={field?.value?.toString()}
                        height="100%"
                    />
                )}
            />
        </StackWithAccordion>
    );

    const simSection = (
        <StackWithAccordion title={text.sections.sim.header} testId="sim-section">
            <Controller
                name="buyerSettings.sim.enable"
                control={control}
                render={({ field }) => (
                    <Checkbox
                        label={text.sections.sim.subSections.simEnable.header}
                        testId="select-sim-check-enableSim"
                        {...field}
                        checked={!!field.value}
                        value={field?.value?.toString()}
                        height="100%"
                    />
                )}
            />
        </StackWithAccordion>
    );

    return (
        <Box width="100%">
            <Stack
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start"
                spacing={4}
                divider={<Divider orientation="horizontal" flexItem />}
            >
                <Typography variant="h1" padding={2}>
                    {text.topHeader}
                </Typography>
                {nextGenSupplierEnablementSection}
                {invoicesSection}
                {posForDeliverySection}
                {poaSection}
                {asnSection}
                {workflowSection}
                {simSection}
            </Stack>
        </Box>
    );
};
